import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';
import useDeepCompareEffect from 'use-deep-compare-effect';

import { loyaltyEffectsQuery } from 'restful/tickets/queries/loyaltyEffectsQuery';
import { restCall } from 'services/api/restful/client';
import { RestApiError } from 'services/api/restful/errors';
import type {
  LoyaltyEffect,
  SimulateLoyaltyCartUpdateInput,
  SimulateLoyaltyCartUpdateInputCartItemsItem,
} from 'services/api/restful/generated/nofomoRestApi.schemas';
import { simulateLoyaltyCartUpdate } from 'services/api/restful/generated/tickets/tickets';

const useSimulateLoyaltyCartMutate = () => {
  const queryClient = useQueryClient();

  const { isPending, isPaused, mutate } = useMutation<
    Array<LoyaltyEffect>,
    RestApiError,
    SimulateLoyaltyCartUpdateInput
  >({
    mutationFn: async (input) => {
      const { data } = await restCall(() => simulateLoyaltyCartUpdate(input));
      return data;
    },
    retry: false,
    onError: () => {
      queryClient.removeQueries({ queryKey: loyaltyEffectsQuery.queryKey });
    },
    onSuccess: (effects) => {
      queryClient.setQueryData(loyaltyEffectsQuery.queryKey, effects);
    },
  });

  return {
    simulateLoyaltyCartUpdate: mutate,
    isLoading: isPending && !isPaused,
  };
};

export const useSimulateLoyaltyCartUpdate = (
  cartId: string | undefined,
  cartItems: Array<SimulateLoyaltyCartUpdateInputCartItemsItem>,
) => {
  const queryClient = useQueryClient();
  const hasTriggeredSimulation = useRef(false);

  const { simulateLoyaltyCartUpdate, isLoading } =
    useSimulateLoyaltyCartMutate();

  const effects = queryClient.getQueryData<Array<LoyaltyEffect>>(
    loyaltyEffectsQuery.queryKey,
  );

  useDeepCompareEffect(() => {
    const shouldSimulate =
      !!cartId && !!cartItems?.length && !hasTriggeredSimulation.current;

    if (shouldSimulate) {
      hasTriggeredSimulation.current = true;
      simulateLoyaltyCartUpdate(
        { cartId, cartItems },
        {
          onError: () => {
            hasTriggeredSimulation.current = false;
          },
        },
      );
    }
  }, [cartId, cartItems, simulateLoyaltyCartUpdate]);

  useDeepCompareEffect(() => {
    hasTriggeredSimulation.current = false;
  }, [cartId, cartItems]);

  return {
    discountValue: effects?.[0]?.value ?? 0,
    isLoading,
  };
};
